import type { AnalyticsSeriesPoint } from '@mg/shared';

/** A numeric metric on a series point — the keys a {@link Sparkline} line can be drawn from. */
export type SeriesMetric = Exclude<keyof AnalyticsSeriesPoint, 'day'>;

/** One plotted line: which field, what to call it, and its colours. */
export interface SparklineMetric {
  key: SeriesMetric;
  label: string;
  /** Tailwind stroke class, e.g. `stroke-brand`. Paired with `swatch` for the legend. */
  stroke: string;
  /** Tailwind background class for the legend swatch, e.g. `bg-brand`. */
  swatch: string;
}

/**
 * The Dashboard's Engagement Overview lines — also the Sparkline default, so callers that don't
 * pass `metrics` are unchanged. Drawn in array order, so the lower-emphasis line is listed first
 * and sits underneath.
 */
export const ENGAGEMENT_METRICS: SparklineMetric[] = [
  { key: 'reach', label: 'Reach', stroke: 'stroke-ink-ghost', swatch: 'bg-ink-ghost' },
  { key: 'engagements', label: 'Engagements', stroke: 'stroke-brand', swatch: 'bg-brand' },
];

/**
 * The Analytics page's Engagement Overview lines.
 *
 * Impressions is the volume metric and runs an order of magnitude above the other two, so it
 * takes the muted gray and is drawn first (underneath); likes and comments are the signal worth
 * reading and take the two accent hues: likes the engagement green (`brand`, the colour the
 * Dashboard's engagement line uses) and comments amber. Green and amber stay well apart under the
 * common colour-vision deficiencies; green and the gray come close for deuteranopes, which is one
 * more reason the legend and the headline totals name every line instead of leaving it to colour.
 *
 * All three share ONE y-scale. That flattens likes and comments when impressions dwarf them, and
 * that is deliberate: rescaling each line to its own peak would make the chart easier to read by
 * making it lie about relative magnitude. The headline totals above the chart carry the numbers.
 */
export const INTERACTION_METRICS: SparklineMetric[] = [
  { key: 'impressions', label: 'Impressions', stroke: 'stroke-ink-faint', swatch: 'bg-ink-faint' },
  { key: 'likes', label: 'Likes', stroke: 'stroke-brand', swatch: 'bg-brand' },
  { key: 'comments', label: 'Comments', stroke: 'stroke-amber-600', swatch: 'bg-amber-600' },
];
